import { useMemo } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

/**
 * ChatMarkdown — slim markdown renderer for assistant chat bubbles.
 *
 * MS190: the chat-concierge Anthropic prompt routinely emits **bold**
 * for headlines / key words. The previous renderer dropped that
 * straight into a <p> with whitespace-pre-wrap, so users saw literal
 * `**asterisks**` instead of bold text — looked broken / unfinished.
 *
 * This component does the absolute minimum needed to make assistant
 * replies feel like polished prose, NOT a wide-open markdown surface:
 *   - Bold (**) and italic (*) inline emphasis
 *   - Inline + autolinked URLs (rel="noopener noreferrer" + target=_blank)
 *   - Unordered + ordered lists for the few cases the LLM enumerates
 *   - Inline `code` for SKU IDs / vendor names if the LLM wraps them
 *   - Paragraph spacing that matches the surrounding bubble rhythm
 *
 * Deliberately NOT supported (to keep the bubble visually calm and
 * prevent the LLM from breaking chat layout):
 *   - Headings (h1-h6) — the bubble itself is the heading; inline H1
 *     inside a chat reply would look broken
 *   - Blockquotes / horizontal rules — too heavy for the chat rhythm
 *   - Images — assistant cards (SkuCard) already handle imagery
 *   - Tables — would overflow the bubble on mobile
 *   - Raw HTML — sanitized away (defense vs. anything weird the model
 *     might emit)
 *
 * Sanitization uses rehype-sanitize's default schema (HTML5 safe
 * subset) further narrowed by the disabled components above.
 *
 * Performance: useMemo'd on `content` so re-renders don't reparse.
 * The chat bubble re-renders frequently during the typewriter
 * reveal, but ChatMarkdown is only mounted AFTER the reveal
 * finishes, so this only fires once per assistant message.
 */
interface ChatMarkdownProps {
  content: string;
}

export function ChatMarkdown({ content }: ChatMarkdownProps) {
  return useMemo(
    () => (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, defaultSchema]]}
        components={{
          // Paragraphs — match the existing bubble's leading-relaxed
          // text-base. mb on all but last so a multi-paragraph reply
          // gets natural spacing without an extra wrapper div.
          p: ({ children, ...props }) => (
            <p
              className="text-base leading-relaxed [&:not(:last-child)]:mb-3"
              {...props}
            >
              {children}
            </p>
          ),
          // Strong (**bold**) — keep the navy/coral colour the
          // surrounding bubble carries, just bump weight.
          strong: ({ children, ...props }) => (
            <strong className="font-semibold" {...props}>
              {children}
            </strong>
          ),
          // Em (*italic*) — same colour, italic style.
          em: ({ children, ...props }) => (
            <em className="italic" {...props}>
              {children}
            </em>
          ),
          // Links — coral underline; external links open in a new
          // tab with rel="noopener". MS268: internal links (the
          // chat-concierge's MS268 prompt instructs the model to
          // emit /browse?category=X handoff links) go through
          // react-router so the chat session is preserved instead of
          // a full-page reload wiping the conversation. Detection:
          // anything that doesn't start with http(s):// or mailto:
          // is treated as internal.
          a: ({ children, href, ...props }) => {
            const isExternal =
              typeof href === "string" &&
              /^(https?:|mailto:|tel:)/i.test(href);
            const isInternal =
              typeof href === "string" && href.startsWith("/") && !isExternal;
            const className =
              "text-coral underline decoration-coral/40 underline-offset-2 hover:decoration-coral";
            if (isInternal) {
              // react-router-dom v6 Link — preserves SPA state.
              // Cast `props` carefully because react-markdown's prop
              // types and Link's overlap closely but aren't identical.
              return (
                <Link to={href!} className={className}>
                  {children}
                </Link>
              );
            }
            return (
              <a
                href={href}
                className={className}
                {...(isExternal
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                {...props}
              >
                {children}
              </a>
            );
          },
          // Lists — tight spacing; the bubble is small, so we don't
          // want list-disc indents that wrap awkwardly.
          ul: ({ children, ...props }) => (
            <ul className="my-2 list-disc pl-5 space-y-1" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="my-2 list-decimal pl-5 space-y-1" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="text-base leading-relaxed" {...props}>
              {children}
            </li>
          ),
          // Inline code (`like this`) — subtle background + mono.
          code: ({ children, ...props }) => (
            <code
              className="rounded bg-charcoal/10 px-1 py-0.5 text-[0.92em] font-mono"
              {...props}
            >
              {children}
            </code>
          ),
          // Block code — wider padding; rare in chat replies but
          // shouldn't break if it appears.
          pre: ({ children, ...props }) => (
            <pre
              className="my-2 overflow-x-auto rounded-lg bg-charcoal/10 p-3 text-sm font-mono"
              {...props}
            >
              {children}
            </pre>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    ),
    [content],
  );
}
